import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "./ThemeProvider";
import { dialogs } from "../context/DialogContext";
import api from "../services/api";
import FichaResumenPropiedad from "./propiedades/FichaResumenPropiedad";
import {
  ensureMensajesInternosAnimCss,
  MSG_INTERNA_ANIM_MS,
  propsDeMensaje,
} from "../utils/mensajesInternosAnim";

const ACCENT = {
  from: "#0d9488",
  to: "#14b8a6",
  soft: "rgba(20,184,166,0.14)",
  border: "rgba(20,184,166,0.4)",
};

const DURACIONES = [
  { id: "10m", label: "10 min" },
  { id: "30m", label: "30 min" },
  { id: "1h", label: "1 h" },
  { id: "4h", label: "4 h" },
  { id: "1d", label: "1 día" },
];

function fmtFecha(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * Popup a pantalla (nuevo mensaje o recordatorio).
 * No se cierra solo: hay que responder, recordar o descartar.
 */
export default function PopupMensajeInterno({
  mensaje,
  esRecordatorio,
  onClose,
  onResponder,
  onRecordatorioProgramado,
}) {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [panelRecordar, setPanelRecordar] = useState(false);
  const [fechaManual, setFechaManual] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [closing, setClosing] = useState(false);
  const [vistaPreviaId, setVistaPreviaId] = useState(null);
  const closingRef = useRef(false);

  useEffect(() => {
    ensureMensajesInternosAnimCss();
  }, []);

  if (!mensaje) return null;

  const propiedades = propsDeMensaje(mensaje);

  const pedirCerrar = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    window.setTimeout(() => {
      onClose?.();
    }, MSG_INTERNA_ANIM_MS);
  };

  const crearRecordatorio = async (payload) => {
    setGuardando(true);
    try {
      const { data } = await api.post(`/mensajes-internos/${mensaje.id}/recordatorios`, payload);
      const rec = data?.data;
      if (rec?.id && rec?.recordar_en) {
        onRecordatorioProgramado?.({
          recordatorio_id: rec.id,
          recordar_en: rec.recordar_en,
          mensaje,
        });
      }
      await dialogs.alert("Recordatorio guardado. Te avisaremos en esa hora.");
      setPanelRecordar(false);
      pedirCerrar();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || "No se pudo guardar el recordatorio");
    } finally {
      setGuardando(false);
    }
  };

  const ui = (
    <div
      className={closing ? "vc-msg-backdrop-out" : "vc-msg-backdrop-in"}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 700,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "72px 16px 24px",
        fontFamily: "'DM Sans', sans-serif",
      }}
      onClick={pedirCerrar}
    >
      <div
        className={closing ? "vc-msg-pop-out" : "vc-msg-pop-in"}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          maxWidth: "100%",
          background: t.bgCard,
          borderRadius: 14,
          border: `1px solid ${ACCENT.border}`,
          boxShadow: "0 24px 60px rgba(0,0,0,0.45), 0 0 40px rgba(20,184,166,0.15)",
          overflow: "hidden",
        }}
      >
        <div style={{ height: 3, background: `linear-gradient(90deg, ${ACCENT.from}, ${ACCENT.to}, ${ACCENT.from})` }} />
        <div style={{ padding: "16px 18px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              {esRecordatorio ? "⏰" : "💬"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: t.textPrimary }}>
                {esRecordatorio ? "Recordatorio" : "Nuevo mensaje interno"}
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
                De <strong style={{ color: t.textPrimary }}>{mensaje.de_nombre || "Compañero"}</strong>
                {" · "}
                {fmtFecha(mensaje.created_at)}
              </div>
            </div>
            <button
              type="button"
              onClick={pedirCerrar}
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: `1px solid ${t.border}`,
                background: isDark ? "#1f2937" : "#f3f4f6",
                color: t.textMuted,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>

          {propiedades.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {propiedades.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setVistaPreviaId(p.id)}
                  style={{
                    textAlign: "left",
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: `1px solid ${ACCENT.border}`,
                    background: ACCENT.soft,
                    color: isDark ? "#5eead4" : ACCENT.from,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  🏠 {p.referencia || "Propiedad"}
                  {p.etiqueta ? ` · ${p.etiqueta}` : ""}
                </button>
              ))}
            </div>
          )}

          <div
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: t.textPrimary,
              whiteSpace: "pre-wrap",
              maxHeight: 220,
              overflowY: "auto",
              padding: "10px 12px",
              borderRadius: 10,
              background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
              border: `1px solid ${t.border}`,
            }}
          >
            {mensaje.cuerpo || (mensaje.adjuntos?.length ? "(Sin texto · ver adjuntos)" : "(Sin texto)")}
          </div>

          {!!mensaje.adjuntos?.length && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              {mensaje.adjuntos.map((a) => (
                <a
                  key={a.id}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: isDark ? "#5eead4" : ACCENT.from,
                    textDecoration: "none",
                    padding: "4px 8px",
                    borderRadius: 6,
                    border: `1px solid ${ACCENT.border}`,
                    background: ACCENT.soft,
                  }}
                >
                  📎 {a.nombre_original || "Adjunto"}
                </a>
              ))}
            </div>
          )}

          {!panelRecordar ? (
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button
                type="button"
                onClick={() => {
                  onResponder?.(mensaje);
                  pedirCerrar();
                }}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})`,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Responder
              </button>
              <button
                type="button"
                onClick={() => setPanelRecordar(true)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${ACCENT.border}`,
                  background: ACCENT.soft,
                  color: isDark ? "#5eead4" : ACCENT.from,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Recordar en…
              </button>
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: t.textMuted, marginBottom: 8 }}>
                Recordar en
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {DURACIONES.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    disabled={guardando}
                    onClick={() => crearRecordatorio({ duracion: d.id })}
                    style={{
                      padding: "7px 12px",
                      borderRadius: 8,
                      border: `1px solid ${ACCENT.border}`,
                      background: isDark ? "rgba(20,184,166,0.1)" : "#fff",
                      color: isDark ? "#5eead4" : ACCENT.from,
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: guardando ? "not-allowed" : "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="datetime-local"
                  value={fechaManual}
                  onChange={(e) => setFechaManual(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: `1px solid ${t.border}`,
                    background: isDark ? "#111827" : "#fff",
                    color: t.textPrimary,
                    fontFamily: "inherit",
                    fontSize: 12,
                  }}
                />
                <button
                  type="button"
                  disabled={guardando || !fechaManual}
                  onClick={() => crearRecordatorio({ recordar_en: new Date(fechaManual).toISOString() })}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "none",
                    background: fechaManual ? `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})` : t.bgInput,
                    color: fechaManual ? "#fff" : t.textMuted,
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: fechaManual ? "pointer" : "not-allowed",
                    fontFamily: "inherit",
                  }}
                >
                  Guardar
                </button>
              </div>
              <button
                type="button"
                onClick={() => setPanelRecordar(false)}
                style={{
                  marginTop: 10,
                  border: "none",
                  background: "transparent",
                  color: t.textMuted,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                ← Volver
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {ui}
      {vistaPreviaId && (
        <FichaResumenPropiedad propiedadId={vistaPreviaId} onClose={() => setVistaPreviaId(null)} />
      )}
    </>,
    document.body
  );
}
